import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Field, Icon, Input } from "../ui";
import { loginUser, fetchBrandConfig, decodeTokenBrandSlug, resetBrandCssVars, BrandConfig } from "../services/api";
import { setBrandConfig } from "../store/slices/brandSlice";
import { setToken, clearToken } from "../store/slices/authSlice";
import { AppDispatch } from "../store";

const Login: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brand, setBrand] = useState<BrandConfig | null>(null);

  useEffect(() => {
    // Clear any stale (possibly white-label) auth token so it cannot keep the
    // UI locked into a previous brand's mode, and reset brand CSS vars so a
    // prior WL palette does not bleed onto the login screen.
    dispatch(clearToken());
    resetBrandCssVars();
    // Show the main brand (esim101) on the login screen.
    fetchBrandConfig("esim101")
      .then(setBrand)
      .catch(() => {});
  }, [dispatch]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const data = await loginUser({ username, password });
      if (data?.token) {
        dispatch(setToken(data.token));
        // Refresh brand config using the logged-in user's brand_slug
        const brandSlug = decodeTokenBrandSlug(data.token);
        // Superadmin (empty brand_slug) → explicitly load the main brand.
        try {
          const cfg = await fetchBrandConfig(brandSlug || "esim101");
          if (cfg.primary_color) document.documentElement.style.setProperty("--color-primary", cfg.primary_color);
          if (cfg.secondary_color) document.documentElement.style.setProperty("--color-secondary", cfg.secondary_color);
          if (cfg.logo_url) document.documentElement.style.setProperty("--brand-logo-url", `url(${cfg.logo_url})`);
          dispatch(setBrandConfig({ name: cfg.name || "eSIM Console", logoUrl: cfg.logo_url, primaryColor: cfg.primary_color, secondaryColor: cfg.secondary_color }));
        } catch {}
        navigate("/dashboard");
      } else {
        setError("No token in response");
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const brandName = brand?.name || "eSIM Console";

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={handleSubmit}>
        {brand?.logo_url ? (
          <img
            src={brand.logo_url}
            alt={brandName}
            style={{ height: 40, objectFit: "contain", maxWidth: 180 }}
          />
        ) : (
          <div className="brand-mark">e</div>
        )}
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em" }}>
            {brandName}
          </div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 2 }}>
            Sign in to continue
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              background: "var(--danger-soft)",
              color: "var(--danger)",
              fontSize: 12.5,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon name="x" size={14} /> {error}
          </div>
        )}

        <Field label="Username">
          <Input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            autoComplete="username"
            autoFocus
          />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </Field>

        <Button
          type="submit"
          variant="primary"
          disabled={submitting || !username || !password}
          style={{ justifyContent: "center", width: "100%", padding: "10px 14px" }}
        >
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </div>
  );
};

export default Login;
